import { AddressSuggestion, FullAddressResponse } from "../types/addressLookup.types";
import { debounce } from "lodash";
import { dropDown } from "../data/interfaces";
import axios from "axios";

// IdealPostcodes API configuration
const IDEALPOSTCODES_API_KEY = process.env.REACT_APP_IDEALPOSTCODES_API_KEY || '';
const IDEALPOSTCODES_BASE_URL = 'https://api.ideal-postcodes.co.uk/v1';

/**
 * Searches for addresses using IdealPostcodes Autocomplete API
 *
 * @param {string} term - The search term used to find addresses.
 * @param {React.Dispatch<React.SetStateAction<dropDown[]>>} storeResultsFn - Function to store results
 * @returns {Promise<AddressSuggestion[]>} A promise that resolves with address suggestions.
 */
const handleAddressLookup = async (
    term: string, 
    storeResultsFn: React.Dispatch<React.SetStateAction<dropDown[]>>
): Promise<AddressSuggestion[]> => {
    try {
        if (!term || term.trim().length === 0) {
            storeResultsFn([]);
            return [];
        }

        if (!IDEALPOSTCODES_API_KEY) {
            console.error('IdealPostcodes API key is missing');
            storeResultsFn([]);
            return [];
        }

        // Call IdealPostcodes autocomplete endpoint
        const response = await axios.get(`${IDEALPOSTCODES_BASE_URL}/autocomplete/addresses`, {
            params: {
                api_key: IDEALPOSTCODES_API_KEY,
                query: term,
                limit: 10
            }
        });

        if (response.data.result?.hits) {
            const suggestions: AddressSuggestion[] = response.data.result.hits.map((hit: any, index: number) => ({
                address: hit.suggestion,
                // Use UDPRN if available, otherwise fall back to the URL from the hit
                placeId: hit.udprn || hit.url || hit.id,
                id: hit.udprn || hit.url || hit.id
            }));

            storeResultsFn(
                suggestions.map((suggestion, index) => ({
                    text: suggestion.address,
                    value: index,
                    data: suggestion.placeId
                }))
            );

            return suggestions;
        } else {
            storeResultsFn([]);
            return [];
        }
    } catch (err: any) {
        console.error('Address lookup error:', err);
        storeResultsFn([]);
        throw new Error(`Failed to search for addresses: ${err.message}`);
    }
}

/**
 * Debounces the address lookup function to prevent it from being called too frequently.
 */
export const searchAddresses = debounce(handleAddressLookup, 500);

/**
 * Retrieves the full address details for a given address ID using IdealPostcodes API
 *
 * @param {string} addressId - The address ID from IdealPostcodes API.
 * @returns {Promise<FullAddressResponse>} A promise that resolves to the full address response.
 * @throws {Error} If the address retrieval fails.
 */
export const getFullAddress = async (addressId: string): Promise<FullAddressResponse> => {
    try {
        if (!IDEALPOSTCODES_API_KEY) {
            throw new Error('IdealPostcodes API key is missing');
        }

        // Call IdealPostcodes address endpoint
        // The autocomplete returns IDs in format that work with /udprn endpoint
        const response = await axios.get(`${IDEALPOSTCODES_BASE_URL}/udprn/${addressId}`, {
            params: {
                api_key: IDEALPOSTCODES_API_KEY
            }
        });

        const address = response.data.result;

        if (!address) {
            throw new Error('No address data returned from IdealPostcodes API');
        }

        // Determine if the address is residential based on organisation_name
        const hasOrganisation = address.organisation_name && address.organisation_name.trim().length > 0;
        const residential = !hasOrganisation;

        // Build address lines based on type
        // NOTE: locality, district, townOrCity, and county are stored in separate structured fields
        // So address lines should only contain the street-level address components
        //let line1, line2, line3, line4;
        
        if (!residential) {
            // Business address: organisation name in line1, street address in line2
            //line1 = address.organisation_name || '';
            
            // Build street address: building number + thoroughfare
            //const streetParts = [];
            //if (address.building_number) streetParts.push(address.building_number);
            //if (address.thoroughfare) streetParts.push(address.thoroughfare);
            //line2 = streetParts.join(' ');
            
            // Sub-building/building names if they exist
            //const buildingParts = [];
            //if (address.sub_building_name) buildingParts.push(address.sub_building_name);
            //if (address.building_name) buildingParts.push(address.building_name);
            //line3 = buildingParts.join(', ');
            
            //line4 = '';
        } else {
            // Residential address: street address in line1
            // Building number + thoroughfare (e.g., "123 High Street")
            //const streetParts = [];
            //if (address.building_number) streetParts.push(address.building_number);
            //if (address.thoroughfare) streetParts.push(address.thoroughfare);
            //line1 = streetParts.join(' ');
            
            // Sub-building/building names if they exist (e.g., "Flat 2B", "Oak House")
            //const buildingParts = [];
            //if (address.sub_building_name) buildingParts.push(address.sub_building_name);
            //if (address.building_name) buildingParts.push(address.building_name);
            //line2 = buildingParts.join(', ');
            
            // Lines 3 and 4 are available for any additional address details if needed
            //line3 = '';
            //line4 = '';
        }

        // Create formatted address array
        /*
        const formattedAddressParts = [
            line1,
            line2,
            line3,
            line4,
            address.post_town,
            address.postcode
        ].filter(part => part && part.trim().length > 0);
            */
        // Create the response object
        const addressData: FullAddressResponse = {
            postcode: address.postcode || '',
            latitude: address.latitude || 0,
            longitude: address.longitude || 0,
            formattedAddress: [],
            buildingName: address.building_name || '',
            buildingNumber: address.building_number || '',
            line1: address.line_1,
            line2: address.line_2,
            line3: address.line_3,
            line4: address.line_4,
            locality: address.locality || '',
            townOrCity: address.post_town || '',
            county: address.county || '',
            district: address.district || '',
            country: address.country || 'England',
            residential: residential,
            thoroughfare: address.thoroughfare || '',
            subBuildingName: address.sub_building_name || '',
            subBuildingNumber: address.sub_building_number || ''
        };

        return addressData;
    } catch (err: any) {
        console.error('Get full address error:', err);
        throw new Error(`Failed to get address details: ${err.response?.data?.message || err.message}`);
    }
}
